import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiEye, FiEyeOff, FiMail, FiLock } from 'react-icons/fi';
import styles from './Login.module.css';
import logoColorido from '../assets/logos/logo.png';
import LoadingSpinner from '../components/LoadingSpinner';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { login, isAuthenticated, cleanState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Limpar estado corrompido ao carregar a página de login
  useEffect(() => {
    if (!isAuthenticated) {
      cleanState();
    }
  }, []);

  // Redireciona se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar mensagem de erro quando usuário começar a digitar
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else {
        setErrorMessage(result.message || 'Credenciais inválidas. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro no login:', error);
      setErrorMessage('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        <div className={styles.logoSection}>
          <img 
            src={logoColorido} 
            alt="Lumi One Clínica" 
            className={styles.logo}
          />
          <h1 className={styles.title}>Bem-vindo ao Lumi One</h1>
          <p className={styles.subtitle}>Sistema de Gestão Clínica</p>
        </div>

        <form onSubmit={handleSubmit} className="form">
          <div className="formGroup required">
            <label htmlFor="email" className="formLabel">
              E-mail
            </label>
            <div className="formInputWrapper">
              <FiMail className="formInputIcon" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                className="formInput"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="formGroup required">
            <label htmlFor="password" className="formLabel">
              Senha
            </label>
            <div className="formInputWrapper hasRightIcon">
              <FiLock className="formInputIcon" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Digite sua senha"
                className="formInput"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="formInputIcon right clickable"
                disabled={isLoading}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          {errorMessage && (
            <div className="formMessage error">
              <i className="fas fa-exclamation-circle"></i>
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="formButton primary large"
            disabled={isLoading || !formData.email || !formData.password}
            style={{ width: '100%' }}
          >
            {isLoading ? (
              <LoadingSpinner size="small" color="white" />
            ) : (
              'Entrar'
            )}
          </button>

          <a href="#" className={styles.forgotPassword}>
            Esqueci minha senha
          </a>
        </form>

        <div className={styles.footer}>
          <div className={styles.companyInfo}>
            <p className={styles.companyName}>Clínica Costa Saúde</p>
            <p className={styles.companyCnpj}>CNPJ: 53.061.003/0001-00</p>
          </div>
          <p className={styles.copyright}>&copy; 2026 Lumi One Clínica. Todos os direitos reservados.</p>
        </div>
      </div>

      {/* Background decorativo */}
      <div className={styles.backgroundDecoration}>
        <div className={styles.circle1}></div>
        <div className={styles.circle2}></div>
        <div className={styles.circle3}></div>
      </div>
    </div>
  );
};

export default Login;